import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { SignaturePad } from "@/components/SignaturePad";
import { VendorCatalog, Thumb } from "@/components/VendorCatalog";
import {
  MagnifyingGlass, Plus, Minus, Trash, SignOut, Package, Buildings,
  ArrowLeft, ArrowRight, CheckCircle, PencilSimple, ClipboardText, Signature, FloppyDisk, Printer,
} from "@phosphor-icons/react";

const T = {
  fr: {
    hello: "Bonjour", newOrder: "Nouvelle commande", myOrders: "Mes commandes", logout: "Déconnexion",
    back: "Retour", next: "Suivant", step: "Étape", client: "Client", products: "Produits", confirm: "Confirmation",
    searchClient: "Rechercher une entreprise…", createClient: "Créer une entreprise", selectClient: "Sélectionner le client",
    companyName: "Nom de l'entreprise", contact: "Personne-contact", phone: "Téléphone", email: "Courriel",
    address: "Adresse", city: "Ville", province: "Province", postal: "Code postal", coop: "Code COOP (optionnel)",
    diffShip: "Adresse de livraison différente", shipAddress: "Adresse de livraison", save: "Enregistrer",
    searchProduct: "Rechercher un produit…", addedProducts: "Produits ajoutés", noProducts: "Aucun produit ajouté",
    qty: "Qté", ordererName: "Nom de la personne qui commande", sign: "Faire signer", signed: "Signé",
    submit: "Soumettre la commande", submitting: "Envoi…", orderSent: "Commande envoyée à l'administration ✅",
    noOrders: "Aucune commande pour l'instant", order: "Commande", status: "Statut", items: "Articles", viewOrder: "Voir",
    needClient: "Sélectionnez une entreprise", needProducts: "Ajoutez au moins un produit", needName: "Entrez le nom du commandeur",
    reviewClient: "Entreprise cliente", createAndUse: "Créer et utiliser", required: "obligatoire",
    sOpen: "Soumise", sPaid: "Facturée", sComplete: "Complétée", sCancel: "Annulée",
    catalog: "Catalogue", all: "Tous", more: "Voir plus", noCatalog: "Aucun produit dans cette catégorie",
    severalFormats: "Plusieurs formats", variation: "Variation", add: "Ajouter", addedToast: "Ajouté", selectedClient: "Client sélectionné", changeClient: "Changer", account: "N° compte", orderGone: "Cette commande a été supprimée par l'administration", signerEmail: "Courriel de la personne qui signe (optionnel)", signerEmailHint: "Un bon de commande signé, sans prix, lui sera envoyé par courriel (copie au vendeur).", badEmail: "Courriel invalide", emailSent: "Bon de commande envoyé par courriel", emailFailed: "Commande envoyée, mais le courriel n'a pas pu partir", draftTitle: "Commande en cours", draftsTitle: "Commandes en préparation", saveExit: "Sauvegarder", draftsVisible: "Visibles par l'administration", print: "Imprimer", nothingToPrint: "Choisissez un client ou ajoutez un produit avant d'imprimer", printFailed: "Impossible de générer le PDF", modified: "modifiée", savedDraft: "Brouillon sauvegardé", otherEmail: "Autre courriel", noEmail: "Aucun", continueDraft: "Continuer", cancelDraft: "Annuler", confirmCancel: "Annuler cette commande en cours ? Les données seront effacées.", confirmNew: "Une commande est déjà en cours. L'annuler et en commencer une nouvelle ?", draftItems: "produit(s)", noClientYet: "Client non choisi", viewSite: "Voir le site", backToCatalog: "Retour", cartLabel: "Panier",
  },
  en: {
    hello: "Hello", newOrder: "New order", myOrders: "My orders", logout: "Log out",
    back: "Back", next: "Next", step: "Step", client: "Client", products: "Products", confirm: "Confirmation",
    searchClient: "Search a company…", createClient: "Create a company", selectClient: "Select client",
    companyName: "Company name", contact: "Contact person", phone: "Phone", email: "Email",
    address: "Address", city: "City", province: "Province", postal: "Postal code", coop: "COOP code (optional)",
    diffShip: "Different shipping address", shipAddress: "Shipping address", save: "Save",
    searchProduct: "Search a product…", addedProducts: "Added products", noProducts: "No product added",
    qty: "Qty", ordererName: "Name of person placing order", sign: "Get signature", signed: "Signed",
    submit: "Submit order", submitting: "Sending…", orderSent: "Order sent to administration ✅",
    noOrders: "No orders yet", order: "Order", status: "Status", items: "Items", viewOrder: "View",
    needClient: "Select a company", needProducts: "Add at least one product", needName: "Enter orderer name",
    reviewClient: "Client company", createAndUse: "Create and use", required: "required",
    sOpen: "Submitted", sPaid: "Invoiced", sComplete: "Completed", sCancel: "Cancelled",
    catalog: "Catalog", all: "All", more: "Show more", noCatalog: "No product in this category",
    severalFormats: "Several formats", variation: "Variation", add: "Add", addedToast: "Added", selectedClient: "Selected client", changeClient: "Change", account: "Account #", orderGone: "This order was deleted by the administration", signerEmail: "Email of the person signing (optional)", signerEmailHint: "A signed purchase order, without prices, will be emailed to them (copy to the vendor).", badEmail: "Invalid email", emailSent: "Purchase order emailed", emailFailed: "Order sent, but the email could not be delivered", draftTitle: "Order in progress", draftsTitle: "Orders in preparation", saveExit: "Save", draftsVisible: "Visible to the administration", print: "Print", nothingToPrint: "Pick a client or add a product before printing", printFailed: "Could not generate the PDF", modified: "edited", savedDraft: "Draft saved", otherEmail: "Other email", noEmail: "None", continueDraft: "Continue", cancelDraft: "Cancel", confirmCancel: "Cancel this order in progress? Data will be erased.", confirmNew: "An order is already in progress. Cancel it and start a new one?", draftItems: "product(s)", noClientYet: "No client selected", viewSite: "View site", backToCatalog: "Back", cartLabel: "Cart",
  },
};
const STATUS_MAP = { open: ["sOpen", "bg-blue-100 text-blue-700"], paid: ["sPaid", "bg-emerald-100 text-emerald-700"], complete: ["sComplete", "bg-violet-100 text-violet-700"], cancel: ["sCancel", "bg-red-100 text-red-700"] };

export default function VendorPage() {
  const { user, logout } = useAuth();
  const { lang, setLang } = useLang();
  const L = T[lang === "en" ? "en" : "fr"];
  const [screen, setScreen] = useState("home");
  const [orders, setOrders] = useState([]);
  const [detail, setDetail] = useState(null);
  const KEY = `vendor_draft_${user?.email || ""}`;
  const EMPTY = { step: 1, customer: null, items: [], ordererName: "" };
  const [draft, setDraft] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const draftIdRef = React.useRef(null);
  const creatingRef = React.useRef(null);
  const timerRef = React.useRef(null);
  const draftRef = React.useRef(null);
  draftRef.current = draft;
  const hasContent = (d) => !!(d && (d.customer || (d.items || []).length || d.ordererName));

  const loadDrafts = () => axios.get(`${API}/vendor/drafts`).then((r) => setDrafts(r.data.drafts || [])).catch(() => {});
  const saveNow = async () => {
    clearTimeout(timerRef.current);
    const d = draftRef.current;
    if (!hasContent(d)) return;
    try {
      if (draftIdRef.current) { await axios.put(`${API}/vendor/drafts/${draftIdRef.current}`, { data: d }); return; }
      if (!creatingRef.current) creatingRef.current = axios.post(`${API}/vendor/drafts`, { data: d });
      const { data } = await creatingRef.current;
      draftIdRef.current = data.id; setDraftId(data.id);
      await axios.put(`${API}/vendor/drafts/${data.id}`, { data: draftRef.current });
    } catch (e) { /* retry on next change */ } finally { creatingRef.current = null; }
  };
  useEffect(() => {
    if (!hasContent(draft)) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(saveNow, 700);
  }, [draft]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const old = (() => { try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; } })();
    if (hasContent(old)) axios.post(`${API}/vendor/drafts`, { data: old }).then(() => { localStorage.removeItem(KEY); loadDrafts(); }).catch(() => {});
    else { localStorage.removeItem(KEY); loadDrafts(); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openDraft = (d) => { draftIdRef.current = d?.id || null; setDraftId(d?.id || null); setDraft(d ? { ...d.data, step: d.data.maxStep || d.data.step || 1 } : { ...EMPTY }); setScreen("new"); };
  const deleteDraft = async (id) => {
    if (!window.confirm(L.confirmCancel)) return false;
    try { await axios.delete(`${API}/vendor/drafts/${id}`); } catch (e) { /* already gone */ }
    if (draftIdRef.current === id) { draftIdRef.current = null; setDraftId(null); setDraft(null); }
    loadDrafts();
    return true;
  };

  const goHome = () => { saveNow().finally(loadDrafts); setScreen("home"); setDetail(null); loadOrders(); };
  const back = () => {
    if (screen === "new" && (draft?.step || 1) > 1) setDraft((d) => ({ ...d, step: d.step - 1 }));
    else if (screen === "detail") { setDetail(null); setScreen("orders"); loadOrders(); }
    else goHome();
  };
  const navBack = () => window.history.back();
  const backRef = React.useRef(back);
  backRef.current = back;
  const screenRef = React.useRef(screen);
  screenRef.current = screen;

  useEffect(() => {
    if (!window.history.state?.vguard) window.history.pushState({ vguard: 1 }, "");
    const onPop = (e) => {
      if (e.state?.vguard) return;
      if (screenRef.current === "home") { window.history.back(); return; }
      backRef.current();
      window.history.pushState({ vguard: 1 }, "");
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const startNew = () => openDraft(null);
  const printDraft = async () => {
    if (!hasContent(draftRef.current)) { toast.error(L.nothingToPrint); return; }
    const w = window.open("", "_blank");
    try {
      await saveNow();
      const url = `${API}/vendor/drafts/${draftIdRef.current}/pdf?t=${Date.now()}`;
      if (w) w.location.href = url; else window.location.href = url;
    } catch (e) { if (w) w.close(); toast.error(L.printFailed); }
  };
  const saveAndExit = async () => {
    if (!hasContent(draftRef.current)) { setScreen("home"); return; }
    await saveNow();
    toast.success(L.savedDraft);
    setDraft(null); draftIdRef.current = null; setDraftId(null);
    loadDrafts(); setScreen("home");
  };
  const cancelDraft = async () => {
    if (draftIdRef.current) { if (await deleteDraft(draftIdRef.current)) { setScreen("home"); } return; }
    if (window.confirm(L.confirmCancel)) { setDraft(null); setScreen("home"); }
  };
  const finishDraft = async () => {
    clearTimeout(timerRef.current);
    const id = draftIdRef.current;
    draftIdRef.current = null; setDraftId(null); setDraft(null);
    if (id) await axios.delete(`${API}/vendor/drafts/${id}`).catch(() => {});
    loadDrafts(); setScreen("home"); loadOrders();
  };

  const loadOrders = () => axios.get(`${API}/vendor/orders`).then((r) => setOrders(r.data.orders || [])).catch(() => {});
  useEffect(() => {
    loadOrders();
    const onVis = () => { if (document.visibilityState === "visible") { loadOrders(); if (screenRef.current === "home") loadDrafts(); } };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    const iv = setInterval(onVis, 30000);
    return () => { document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", onVis); clearInterval(iv); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#f4f7f9]">
      <header className="sticky top-0 z-30 flex items-center justify-between bg-[#2e3a47] px-4 py-3 text-white shadow">
        <div className="flex items-center gap-2">
          {screen !== "home" && (
            <button data-testid="vendor-back" onClick={navBack} className="flex items-center gap-1 rounded-lg bg-white/10 px-2.5 py-1.5 text-xs font-bold uppercase hover:bg-white/20"><ArrowLeft size={18} weight="bold" /> {L.back}</button>
          )}
          <span className={`whitespace-nowrap text-xs font-bold uppercase tracking-wider sm:text-sm ${screen !== "home" ? "hidden sm:inline" : ""}`}>Servitech Xpert</span>
        </div>
        <div className="flex items-center gap-3">
          <a data-testid="vendor-view-site" href="/" className="whitespace-nowrap text-xs font-bold uppercase opacity-80 hover:opacity-100">{L.viewSite}</a>
          <button data-testid="vendor-lang" onClick={() => setLang(lang === "en" ? "fr" : "en")} className="text-xs font-bold uppercase opacity-80 hover:opacity-100">{lang === "en" ? "FR" : "EN"}</button>
          <button data-testid="vendor-logout" onClick={logout} className="flex items-center gap-1 whitespace-nowrap text-xs font-bold uppercase opacity-80 hover:opacity-100"><SignOut size={16} weight="bold" /> {L.logout}</button>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-6">
        {screen === "home" && (
          <div data-testid="vendor-home">
            <h1 className="mb-1 text-2xl font-bold text-[#2e3a47]">{L.hello}, {(user?.name || "").split(" ")[0] || ""}</h1>
            <p className="mb-6 text-sm text-muted-foreground">{user?.email}</p>
            <div className="mb-6 grid grid-cols-2 gap-3">
              <button data-testid="vendor-new-order" onClick={startNew}
                className="flex flex-col items-start gap-2 rounded-xl bg-primary p-4 text-left text-white shadow-lg transition-transform duration-200 active:scale-95 sm:flex-row sm:items-center sm:gap-4 sm:p-6">
                <ClipboardText size={32} weight="bold" />
                <div className="text-base font-bold leading-tight sm:text-xl">{L.newOrder}</div>
              </button>
              <button data-testid="vendor-my-orders" onClick={() => { loadOrders(); setScreen("orders"); }}
                className="flex flex-col items-start gap-2 rounded-xl border border-border bg-white p-4 text-left text-[#2e3a47] shadow transition-transform duration-200 active:scale-95 sm:flex-row sm:items-center sm:gap-4 sm:p-6">
                <Package size={32} weight="bold" className="text-primary" />
                <div><div className="text-base font-bold leading-tight sm:text-xl">{L.myOrders}</div><div className="text-sm text-muted-foreground">{orders.length}</div></div>
              </button>
            </div>
            {drafts.length > 0 && (
              <div data-testid="vendor-drafts" className="mb-5">
                <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-amber-700">{L.draftsTitle} ({drafts.length})</div>
                <div data-testid="vendor-drafts-visibility" className="mb-2 text-[11px] text-muted-foreground">{L.draftsVisible}</div>
                <div className="space-y-3">
                  {drafts.map((d) => (
                    <div key={d.id} data-testid={`vendor-draft-${d.id}`} className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 shadow-sm">
                      <div className="font-bold text-[#2e3a47]">{d.data.customer?.succursale || d.data.customer?.business_name || L.noClientYet}</div>
                      {d.data.customer && <div className="text-xs text-[#2e3a47]">{[d.data.customer.address, d.data.customer.city].filter(Boolean).join(", ")}</div>}
                      <div className="text-xs text-muted-foreground">{(d.data.items || []).length} {L.draftItems} · {L.step} {d.data.maxStep || d.data.step || 1}/3 · {L.modified} {new Date(d.updated_at).toLocaleString(lang === "en" ? "en-CA" : "fr-CA", { dateStyle: "short", timeStyle: "short" })}</div>
                      <div className="mt-3 flex gap-3">
                        <button data-testid={`vendor-draft-delete-${d.id}`} onClick={() => deleteDraft(d.id)} className="rounded-lg border border-red-300 bg-white px-4 py-2.5 text-sm font-bold uppercase text-red-600"><Trash size={16} weight="bold" /></button>
                        <button data-testid={`vendor-draft-continue-${d.id}`} onClick={() => openDraft(d)} className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-primary px-4 py-2.5 text-sm font-bold uppercase text-white shadow">{L.continueDraft} <ArrowRight size={16} weight="bold" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {screen === "new" && <NewOrder L={L} lang={lang} draft={draft || EMPTY} setDraft={setDraft} onBack={navBack} onCancel={cancelDraft} onDone={finishDraft} onSave={saveAndExit} onPrint={printDraft} saved={!!draftId} />}

        {screen === "orders" && (
          <div data-testid="vendor-orders-list">
            <h1 className="mb-4 text-xl font-bold text-[#2e3a47]">{L.myOrders}</h1>
            {orders.length === 0 ? <div className="rounded-lg border border-dashed border-border bg-white p-10 text-center text-sm text-muted-foreground">{L.noOrders}</div> : (
              <div className="space-y-3">
                {orders.map((o) => {
                  const [sk, scls] = STATUS_MAP[o.manual_status] || STATUS_MAP.open;
                  return (
                    <button key={o.id} data-testid={`vendor-order-${o.id}`} onClick={() => axios.get(`${API}/vendor/orders/${o.id}`).then((r) => { setDetail(r.data); setScreen("detail"); }).catch(() => { toast.error(L.orderGone); loadOrders(); })}
                      className="flex w-full items-center justify-between rounded-lg border border-border bg-white p-4 text-left shadow-sm transition active:scale-[0.99]">
                      <div className="min-w-0">
                        <div className="truncate font-bold text-[#2e3a47]">{o.business_name || "—"}</div>
                        <div className="text-xs text-muted-foreground">{(o.created_at || "").slice(0, 10)} · {o.item_count} {L.items}{o.invoice_number ? ` · ${o.invoice_number}` : ""}</div>
                      </div>
                      <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold ${scls}`}>{L[sk]}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {screen === "detail" && detail && (
          <div data-testid="vendor-order-detail">
            <div className="mb-1 flex items-start justify-between gap-3">
              <h1 className="text-xl font-bold text-[#2e3a47]">{detail.business_name}</h1>
              <a data-testid="vendor-order-print" href={`${API}/vendor/orders/${detail.id}/pdf`} target="_blank" rel="noreferrer"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border-2 border-[#2e3a47] bg-white px-3 py-2 text-xs font-bold uppercase tracking-wider text-[#2e3a47] shadow-sm active:scale-95">
                <Printer size={16} weight="bold" /> {L.print}
              </a>
            </div>
            <p className="mb-4 text-xs text-muted-foreground">{(detail.created_at || "").slice(0, 10)} · {(() => { const [sk] = STATUS_MAP[detail.manual_status] || STATUS_MAP.open; return L[sk]; })()}</p>
            <div className="divide-y divide-border rounded-lg border border-border bg-white">
              {(detail.items || []).map((it, i) => (
                <div key={i} data-testid={`vendor-detail-item-${i}`} className="flex items-center gap-3 p-3">
                  <Thumb src={it.image} className="h-12 w-12 shrink-0 rounded border border-border p-0.5" />
                  <div className="min-w-0 flex-1"><div className="truncate text-sm font-medium">{it.name}</div><div className="font-mono text-[11px] text-primary">{it.product_code}{it.variant_value ? ` · ${it.variant_value}` : ""}</div></div>
                  <div className="shrink-0 text-sm font-bold">×{it.qty}</div>
                </div>
              ))}
            </div>
            {detail.orderer_name && <p className="mt-4 text-sm"><b>{L.ordererName}:</b> {detail.orderer_name}</p>}
            {detail.signature_url && <img loading="lazy" decoding="async" src={detail.signature_url} alt="signature" className="mt-2 h-24 rounded border border-border bg-white object-contain p-2" />}
          </div>
        )}
      </main>
    </div>
  );
}

function NewOrder({ L, lang, draft, setDraft, onBack, onCancel, onDone, onSave, onPrint, saved }) {
  const set = (k) => (v) => setDraft((d) => { const base = d || { step: 1, customer: null, items: [], ordererName: "" }; return { ...base, [k]: typeof v === "function" ? v(base[k]) : v }; });
  const { step, customer, items, ordererName } = draft;
  const signerEmail = draft.signerEmail || "";
  const setSignerEmail = set("signerEmail");
  const setCustomer = (v) => setDraft((d) => ({ ...(d || {}), customer: v, signerEmailMode: undefined, signerEmail: "" }));
  const setStep = set("step"), setItems = set("items"), setOrdererName = set("ordererName");
  const [signature, setSignature] = useState("");
  const [sigOpen, setSigOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const custEmails = [customer?.email, customer?.email2].map((e) => (e || "").trim()).filter((e, i, a) => e && a.indexOf(e) === i);
  const emailMode = draft.signerEmailMode;
  useEffect(() => {
    if (step === 3 && emailMode === undefined) {
      if (custEmails[0]) setDraft((d) => ({ ...d, signerEmailMode: "e0", signerEmail: custEmails[0] }));
      else setDraft((d) => ({ ...d, signerEmailMode: "other" }));
    }
  }, [step, emailMode]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (step > (draft.maxStep || 1)) setDraft((d) => ({ ...d, maxStep: step }));
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps
  const pickEmail = (mode, value) => setDraft((d) => ({ ...d, signerEmailMode: mode, signerEmail: value }));

  const steps = [L.client, L.products, L.confirm];

  const next = () => {
    if (step === 1 && !customer) { toast.error(L.needClient); return; }
    if (step === 2 && items.length === 0) { toast.error(L.needProducts); return; }
    setStep((s) => Math.min(3, s + 1));
  };

  const submit = async () => {
    if (!ordererName.trim()) { toast.error(L.needName); return; }
    if (signerEmail.trim() && !/^[^@\s]+@[^@\s]+\.[a-zA-Z]{2,}$/.test(signerEmail.trim())) { toast.error(L.badEmail); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/vendor/orders`, {
        signer_email: signerEmail.trim(),
        customer_id: customer.id,
        items: items.map((it) => ({ product_id: it.product_id, product_code: it.product_code, name: it.name, variant_value: it.variant_value, variation_label: it.variation_label, qty: it.qty })),
        orderer_name: ordererName.trim(),
        signature,
      });
      toast.success(L.orderSent);
      if (data.email_status === "sent") toast.success(L.emailSent);
      else if (data.email_status === "failed") toast.error(L.emailFailed);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="vendor-new-order-wizard">
      <div className="mb-5 flex items-center gap-2">
        {steps.map((s, i) => (
          <div key={i} className="flex flex-1 flex-col items-center">
            <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${step >= i + 1 ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`}>{i + 1}</div>
            <span className={`mt-1 text-[10px] font-bold uppercase ${step >= i + 1 ? "text-primary" : "text-muted-foreground"}`}>{s}</span>
          </div>
        ))}
      </div>
      <div className="-mt-3 mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button data-testid="vendor-save-draft" onClick={onSave}
            className="inline-flex items-center gap-1.5 rounded-lg border-2 border-primary bg-white px-3 py-2 text-xs font-bold uppercase tracking-wider text-primary shadow-sm active:scale-95">
            <FloppyDisk size={16} weight="bold" /> {L.saveExit}
          </button>
          <button data-testid="vendor-print-draft" onClick={onPrint}
            className="inline-flex items-center gap-1.5 rounded-lg border-2 border-[#2e3a47] bg-white px-3 py-2 text-xs font-bold uppercase tracking-wider text-[#2e3a47] shadow-sm active:scale-95">
            <Printer size={16} weight="bold" /> {L.print}
          </button>
          <span data-testid="vendor-draft-saved" className="text-[11px] font-semibold text-emerald-600">{saved ? "✓" : ""}</span>
        </div>
        <button data-testid="vendor-cancel-order" onClick={onCancel} className="text-xs font-bold uppercase tracking-wider text-red-600 underline">{L.cancelDraft}</button>
      </div>

      {step === 1 && <StepClient L={L} customer={customer} setCustomer={setCustomer} />}
      {step === 2 && <StepProducts L={L} lang={lang} items={items} setItems={setItems} />}
      {step === 3 && (
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-white p-4">
            <div className="text-[11px] font-bold uppercase tracking-wider text-primary">{L.reviewClient}</div>
            <div className="mt-1 font-bold text-[#2e3a47]">{customer?.business_name}</div>
            <div className="text-xs text-muted-foreground">{customer?.city}{customer?.coop_code ? ` · ${customer.coop_code}` : ""}</div>
          </div>
          <div className="divide-y divide-border rounded-lg border border-border bg-white">
            {items.map((it, i) => (
              <div key={i} data-testid={`vendor-confirm-item-${i}`} className="flex items-center gap-3 p-3">
                <Thumb src={it.image} className="h-12 w-12 shrink-0 rounded border border-border p-0.5" />
                <div className="min-w-0 flex-1"><div className="truncate text-sm font-medium">{it.name}</div><div className="font-mono text-[11px] text-primary">{it.product_code}{it.variant_value ? ` · ${it.variant_value}` : ""}</div></div>
                <div className="shrink-0 text-sm font-bold">×{it.qty}</div>
              </div>
            ))}
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">{L.ordererName} *</label>
            <input data-testid="vendor-orderer-name" value={ordererName} onChange={(e) => setOrdererName(e.target.value)}
              className="w-full rounded-lg border border-border bg-white px-3 py-3 text-base outline-none focus:border-primary" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">{L.signerEmail}</label>
            <div className="mb-2 flex flex-wrap gap-2">
              {custEmails.map((e, i) => (
                <button key={e} data-testid={`vendor-signer-email-opt-${i}`} onClick={() => pickEmail(`e${i}`, e)}
                  className={`max-w-full truncate rounded-full border px-3 py-2 text-xs font-bold ${emailMode === `e${i}` ? "border-primary bg-primary text-white" : "border-border bg-white text-[#2e3a47]"}`}>{e}</button>
              ))}
              <button data-testid="vendor-signer-email-opt-other" onClick={() => pickEmail("other", "")}
                className={`rounded-full border px-3 py-2 text-xs font-bold ${emailMode === "other" ? "border-primary bg-primary text-white" : "border-border bg-white text-[#2e3a47]"}`}>{L.otherEmail}</button>
              <button data-testid="vendor-signer-email-opt-none" onClick={() => pickEmail("none", "")}
                className={`rounded-full border px-3 py-2 text-xs font-bold ${emailMode === "none" ? "border-primary bg-primary text-white" : "border-border bg-white text-muted-foreground"}`}>{L.noEmail}</button>
            </div>
            {emailMode === "other" && (
              <input data-testid="vendor-signer-email" type="email" inputMode="email" autoComplete="off" value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)}
                className="w-full rounded-lg border border-border bg-white px-3 py-3 text-base outline-none focus:border-primary" />
            )}
            <p className="mt-1 text-xs text-muted-foreground">{L.signerEmailHint}</p>
          </div>
          <button data-testid="vendor-sign-button" onClick={() => setSigOpen(true)}
            className={`flex w-full items-center justify-center gap-2 rounded-lg border py-3 text-sm font-bold uppercase tracking-wider transition ${signature ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-primary text-primary"}`}>
            {signature ? <CheckCircle size={18} weight="bold" /> : <Signature size={18} weight="bold" />} {signature ? L.signed : L.sign}
          </button>
          <div className="flex gap-3">
          <button data-testid="vendor-prev-confirm" onClick={onBack} className="flex items-center gap-1 rounded-lg border border-border bg-white px-5 py-4 text-sm font-bold uppercase text-muted-foreground"><ArrowLeft size={16} weight="bold" /> {L.back}</button>
          <button data-testid="vendor-submit" onClick={submit} disabled={submitting}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary py-4 text-base font-bold uppercase tracking-wider text-white shadow-lg transition active:scale-95 disabled:opacity-60">
            {submitting ? L.submitting : L.submit}
          </button>
          </div>
          <SignaturePad open={sigOpen} onClose={() => setSigOpen(false)} defaultName={ordererName}
            onSave={(dataUrl, name) => { setSignature(dataUrl); if (name && !ordererName) setOrdererName(name); setSigOpen(false); }} />
        </div>
      )}

      {step < 3 && (
        <div className="sticky bottom-0 z-20 -mx-4 mt-6 flex gap-3 border-t border-border bg-[#f4f7f9]/95 px-4 py-3 backdrop-blur">
          {<button data-testid="vendor-prev" onClick={onBack} className="flex items-center gap-1 rounded-lg border border-border bg-white px-5 py-3 text-sm font-bold uppercase text-muted-foreground"><ArrowLeft size={16} weight="bold" /> {L.back}</button>}
          <button data-testid="vendor-next" onClick={next} className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-primary px-5 py-3 text-sm font-bold uppercase text-white">{L.next}{step === 2 && items.length > 0 ? ` (${items.length})` : ""} <ArrowRight size={16} weight="bold" /></button>
        </div>
      )}
    </div>
  );
}

function StepClient({ L, customer, setCustomer }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ business_name: "", general_contact: "", phone: "", email: "", address: "", city: "", province: "", postal_code: "", coop_client_code: "", diffShip: false, ship_address: "", ship_city: "", ship_province: "", ship_postal: "" });

  useEffect(() => {
    if (!q.trim() || creating) { setResults([]); return; }
    let live = true;
    const tm = setTimeout(() => axios.get(`${API}/vendor/customers`, { params: { q } }).then((r) => { if (live) setResults(r.data.customers || []); }).catch(() => {}), 250);
    return () => { live = false; clearTimeout(tm); };
  }, [q, creating]);

  const createCompany = async () => {
    if (!form.business_name.trim()) { toast.error(L.companyName + " " + L.required); return; }
    try {
      const payload = { ...form };
      if (!form.diffShip) { payload.ship_address = form.address; payload.ship_city = form.city; payload.ship_province = form.province; payload.ship_postal = form.postal_code; }
      const { data } = await axios.post(`${API}/vendor/customers`, payload);
      setCustomer({ id: data.id, business_name: data.business_name || form.business_name, address: form.address, city: form.city, province: form.province, postal_code: form.postal_code, coop_code: form.coop_client_code, contact: form.general_contact, phone: form.phone });
      setCreating(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  if (customer) return (
    <div data-testid="vendor-selected-client" className="rounded-lg border-2 border-emerald-400 bg-emerald-50 p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">{L.selectedClient}</span>
        <button data-testid="vendor-change-client" onClick={() => setCustomer(null)} className="text-xs font-bold uppercase text-primary underline">{L.changeClient}</button>
      </div>
      <ClientDetails c={customer} L={L} />
    </div>
  );

  const F = ({ k, label, full }) => (
    <div className={full ? "col-span-2" : ""}>
      <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{label}</label>
      <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`vendor-company-${k}`}
        className="w-full rounded-lg border border-border bg-white px-3 py-2.5 text-base outline-none focus:border-primary" />
    </div>
  );

  if (creating) return (
    <div data-testid="vendor-create-company" className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        {F({ k: "business_name", label: `${L.companyName} *`, full: true })}
        {F({ k: "general_contact", label: L.contact, full: true })}
        {F({ k: "phone", label: L.phone })}
        {F({ k: "email", label: L.email })}
        {F({ k: "address", label: L.address, full: true })}
        {F({ k: "city", label: L.city })}
        {F({ k: "province", label: L.province })}
        {F({ k: "postal_code", label: L.postal })}
        {F({ k: "coop_client_code", label: L.coop })}
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="vendor-diff-ship" checked={form.diffShip} onChange={(e) => setForm({ ...form, diffShip: e.target.checked })} /> {L.diffShip}</label>
      {form.diffShip && (
        <div className="grid grid-cols-2 gap-3 rounded-lg bg-muted/40 p-3">
          {F({ k: "ship_address", label: L.shipAddress, full: true })}
          {F({ k: "ship_city", label: L.city })}
          {F({ k: "ship_province", label: L.province })}
          {F({ k: "ship_postal", label: L.postal })}
        </div>
      )}
      <div className="flex gap-3">
        <button onClick={() => setCreating(false)} className="rounded-lg border border-border bg-white px-4 py-2.5 text-sm font-bold uppercase text-muted-foreground">{L.back}</button>
        <button data-testid="vendor-create-company-save" onClick={createCompany} className="flex-1 rounded-lg bg-primary px-4 py-2.5 text-sm font-bold uppercase text-white">{L.createAndUse}</button>
      </div>
    </div>
  );

  return (
    <div data-testid="vendor-step-client">
      <div className="flex items-center gap-2 rounded-lg border border-border bg-white px-3">
        <MagnifyingGlass size={18} className="text-muted-foreground" />
        <input data-testid="vendor-client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={L.searchClient} className="w-full bg-transparent py-3 text-base outline-none" />
      </div>
      <div className="mt-2 space-y-2">
        {results.map((c) => (
          <button key={c.id} data-testid={`vendor-client-${c.id}`} onClick={() => setCustomer(c)} className="flex w-full items-start gap-3 rounded-lg border border-border bg-white p-3 text-left shadow-sm transition-colors hover:border-primary active:scale-[0.99]">
            <Buildings size={22} className="mt-0.5 shrink-0 text-primary" />
            <ClientDetails c={c} L={L} />
          </button>
        ))}
      </div>
      <button data-testid="vendor-create-company-btn" onClick={() => setCreating(true)} className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-primary py-3 text-sm font-bold uppercase tracking-wider text-primary">
        <Plus size={16} weight="bold" /> {L.createClient}
      </button>
    </div>
  );
}

function ClientDetails({ c, L }) {
  const name = c.succursale || c.business_name;
  const parent = c.entreprise && c.entreprise.toLowerCase() !== (name || "").toLowerCase() ? c.entreprise : "";
  const cityLine = [c.city, c.province, c.postal_code].filter(Boolean).join(" ");
  const meta = [c.coop_code && `COOP: ${c.coop_code}`, c.account_number && `${L.account}: ${c.account_number}`].filter(Boolean);
  const contact = [c.contact && c.contact !== name ? c.contact : "", c.phone].filter(Boolean);
  return (
    <div className="min-w-0 flex-1" data-testid={`vendor-client-details-${c.id}`}>
      <div className="font-bold leading-snug text-[#2e3a47]">{name}</div>
      {parent && <div className="text-xs font-medium text-primary">{parent}</div>}
      {(c.address || cityLine) && (
        <div className="mt-1 text-sm leading-snug text-[#2e3a47]">
          {c.address}{c.address && cityLine ? <br /> : null}{cityLine}
        </div>
      )}
      {meta.length > 0 && <div className="mt-1 font-mono text-[11px] text-muted-foreground">{meta.join(" · ")}</div>}
      {contact.length > 0 && <div className="text-xs text-muted-foreground">{contact.join(" · ")}</div>}
    </div>
  );
}

function StepProducts({ L, lang, items, setItems }) {
  const [q, setQ] = useState("");
  const searchRef = React.useRef(null);
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    let live = true;
    const tm = setTimeout(() => axios.get(`${API}/vendor/products`, { params: { q } }).then((r) => { if (live) setResults(r.data.products || []); }).catch(() => {}), 250);
    return () => { live = false; clearTimeout(tm); };
  }, [q]);

  const add = (p, qty = 1) => {
    const n = (typeof p.name === "string" ? p.name : (p.name?.[lang] || p.name?.fr)) || "";
    setItems((prev) => prev.some((it) => it.product_id === p.product_id)
      ? prev.map((it) => it.product_id === p.product_id ? { ...it, qty: Math.round((it.qty + qty) * 100) / 100 } : it)
      : [...prev, { product_id: p.product_id, product_code: p.product_code, name: n, variant_value: p.variant_value || "", variation_label: p.variation_label || "", image: p.image || "", qty }]);
    toast.success(`${L.addedToast} : ${p.product_code}${p.variant_value ? ` · ${p.variant_value}` : ""} ×${qty}`);
    setQ(""); setResults([]);
  };
  const setQty = (pid, qty) => setItems(items.map((it) => it.product_id === pid ? { ...it, qty: Math.max(0.01, qty) } : it));
  const remove = (pid) => setItems(items.filter((it) => it.product_id !== pid));

  return (
    <div data-testid="vendor-step-products">
      <div className="flex items-center gap-2 rounded-lg border border-border bg-white px-3">
        <MagnifyingGlass size={18} className="text-muted-foreground" />
        <input ref={searchRef} data-testid="vendor-product-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={L.searchProduct} className="w-full bg-transparent py-3 text-base outline-none" />
      </div>
      {results.length > 0 && (
        <div className="mt-2 space-y-2">
          {results.map((p) => (
            <button key={p.product_id} data-testid={`vendor-product-${p.product_id}`} onMouseDown={(e) => e.preventDefault()} onClick={() => { add(p); searchRef.current?.focus(); }} className="flex w-full items-center gap-3 rounded-lg border border-border bg-white p-3 text-left active:scale-[0.99]">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden border border-border bg-white">{p.image ? <img loading="lazy" decoding="async" src={p.image} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <Package size={18} className="text-muted-foreground" />}</div>
              <div className="min-w-0 flex-1"><div className="truncate text-sm font-medium">{p.name[lang] || p.name.fr}</div><div className="font-mono text-[11px] text-primary">{p.product_code}{p.variant_value ? ` · ${p.variant_value}` : ""}</div></div>
              <Plus size={20} weight="bold" className="shrink-0 text-primary" />
            </button>
          ))}
        </div>
      )}
      <div className="mt-5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{L.addedProducts} ({items.length})</div>
      {items.length === 0 ? <div className="mt-2 rounded-lg border border-dashed border-border bg-white p-6 text-center text-sm text-muted-foreground">{L.noProducts}</div> : (
        <div className="mt-2 divide-y divide-border rounded-lg border border-border bg-white">
          {items.map((it) => (
            <div key={it.product_id} data-testid={`vendor-cart-${it.product_id}`} className="flex items-center gap-3 p-3">
              <Thumb src={it.image} className="h-12 w-12 shrink-0 rounded border border-border p-0.5" />
              <div className="min-w-0 flex-1"><div className="truncate text-sm font-medium">{it.name}</div><div className="font-mono text-[11px] text-primary">{it.product_code}{it.variant_value ? ` · ${it.variant_value}` : ""}</div></div>
              <div className="flex shrink-0 items-center gap-1">
                <button data-testid={`vendor-qty-minus-${it.product_id}`} onClick={() => setQty(it.product_id, Math.round((it.qty - 1) * 100) / 100)} className="flex h-8 w-8 items-center justify-center rounded border border-border"><Minus size={14} weight="bold" /></button>
                <input data-testid={`vendor-qty-${it.product_id}`} type="number" step="0.01" value={it.qty} onChange={(e) => setQty(it.product_id, parseFloat(e.target.value) || 0.01)} className="w-14 rounded border border-border py-1 text-center text-sm outline-none focus:border-primary" />
                <button data-testid={`vendor-qty-plus-${it.product_id}`} onClick={() => setQty(it.product_id, Math.round((it.qty + 1) * 100) / 100)} className="flex h-8 w-8 items-center justify-center rounded border border-border"><Plus size={14} weight="bold" /></button>
              </div>
              <button data-testid={`vendor-remove-${it.product_id}`} onClick={() => remove(it.product_id)} className="shrink-0 text-red-500"><Trash size={16} weight="bold" /></button>
            </div>
          ))}
        </div>
      )}
      {!q.trim() && <VendorCatalog L={L} lang={lang} items={items} onAdd={add} />}
    </div>
  );
}
